// @workflow platform.viewer
/**
 * Cutting the 3D model down: crop to levels, the crop box, and the scaffold boxes.
 * Every operation forwards to today's implementation unchanged; the comment on each names it.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { sameId } from './ids.js';

/** @param {object} ctx see core.js `createViewer` */
export function createCropOps(ctx) {
  const { store, current, service } = ctx;
  const cropExtension = () => service().getCropping3DExtension();
  const scaffoldExtension = () => (current() ? service().getScaffoldBoxExtension() : null);

  return {
    /**
     * forgeService.cropLevelIn3D for the facility's levels with these ids (store.state.levels);
     * an empty list removes the cut. Ids that match no level are answered, not guessed.
     * @param {Array<string|number>} levelIds
     * @returns {{ cropped: Array<string|number>, missing: Array<string|number> } | null} null without a viewer
     */
    cropLevels(levelIds) {
      if (!current()) return null;
      const ids = Array.isArray(levelIds) ? levelIds : [];
      const levels = store.state.levels || [];
      const found = levels.filter((level) => level && ids.some((id) => sameId(level.id, id)));
      const missing = ids.filter((id) => !levels.some((level) => level && sameId(level.id, id)));
      if (ids.length && !found.length) return { cropped: [], missing };
      service().cropLevelIn3D(found);
      return { cropped: found.map((level) => level.id), missing };
    },
    /** Cropping3DExtension.clearCropBox. */
    clearCropBox() {
      const ext = cropExtension();
      if (!ext || typeof ext.clearCropBox !== 'function') return false;
      ext.clearCropBox();
      return true;
    },
    /** Cropping3DExtension.loadCropVolumeForTag (an AVA Inspect tag's saved crop volume). */
    loadTagCropVolume(tag) {
      const ext = cropExtension();
      if (!ext || typeof ext.loadCropVolumeForTag !== 'function') return false;
      ext.loadCropVolumeForTag(tag);
      return true;
    },
    /** ScaffoldBoxExtension.setIsolation: show only these scaffold boxes. */
    isolateScaffolds(scaffoldIds) {
      const ext = scaffoldExtension();
      if (!ext) return false;
      ext.setIsolation(scaffoldIds);
      return true;
    },
    /** ScaffoldBoxExtension.setCustomColor. */
    colourScaffold(scaffoldId, colorHex) {
      const ext = scaffoldExtension();
      if (!ext) return false;
      ext.setCustomColor(scaffoldId, colorHex);
      return true;
    },
    /** ScaffoldBoxExtension.resetColoring. */
    resetScaffoldColours() {
      const ext = scaffoldExtension();
      if (!ext) return false;
      ext.resetColoring();
      return true;
    },
  };
}
